import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, RotateCcw, Check } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PenaltiesTab() {
  const [league, setLeague] = useState('racing');
  const [penalties, setPenalties] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchPenalties();
    // eslint-disable-next-line
  }, [league]);

  const fetchPenalties = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/admin/selection-penalties?league=${league}`,
        { withCredentials: true }
      );
      setPenalties(res.data || []);
    } catch (e) {
      toast.error('Failed to load penalties');
    } finally {
      setLoading(false);
    }
  };

  const togglePenalty = async (row, waived) => {
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/selection-penalties/waive`,
        {
          user_id: row.user_id,
          league,
          round_window: row.round_window,
          waived,
        },
        { withCredentials: true }
      );
      toast.success(waived ? 'Penalty waived' : 'Penalty re-applied');
      fetchPenalties();
    } catch (e) {
      toast.error('Failed to update penalty');
    }
  };

  const totalActive = penalties.filter((p) => !p.waived).length;
  const totalWaived = penalties.filter((p) => p.waived).length;

  return (
    <div className="space-y-6" data-testid="penalties-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-orange-400" />
            Selection-Change Penalties
          </h2>
          <p className="text-zinc-400 text-sm mt-1">
            -50 points per round window when a player edits an existing team. First save in a window is free; capped at one penalty per round.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setLeague('racing')}
            className={`px-4 py-2 text-sm font-bold ${
              league === 'racing' ? 'bg-orange-500 text-white' : 'bg-zinc-800 text-zinc-400'
            }`}
            data-testid="penalties-league-racing"
          >
            Racing
          </button>
          <button
            onClick={() => setLeague('moto')}
            className={`px-4 py-2 text-sm font-bold ${
              league === 'moto' ? 'bg-orange-500 text-white' : 'bg-zinc-800 text-zinc-400'
            }`}
            data-testid="penalties-league-moto"
          >
            Moto
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="bg-zinc-900 border border-zinc-800 p-4">
          <p className="text-xs text-zinc-400">Active Penalties</p>
          <p className="text-2xl font-bold text-orange-400" data-testid="penalties-active-count">{totalActive}</p>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 p-4">
          <p className="text-xs text-zinc-400">Waived</p>
          <p className="text-2xl font-bold text-emerald-400" data-testid="penalties-waived-count">{totalWaived}</p>
        </div>
      </div>

      <div className="bg-zinc-900 border border-zinc-800">
        {loading ? (
          <p className="p-6 text-zinc-400">Loading...</p>
        ) : penalties.length === 0 ? (
          <p className="p-6 text-zinc-400">No selection-change penalties recorded for this league yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-950 text-zinc-400 text-xs uppercase">
                <tr>
                  <th className="text-left px-4 py-3">Player</th>
                  <th className="text-left px-4 py-3">Round Window</th>
                  <th className="text-left px-4 py-3">Edits</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-left px-4 py-3">Last Edit</th>
                  <th className="text-right px-4 py-3">Action</th>
                </tr>
              </thead>
              <tbody>
                {penalties.map((p) => (
                  <tr
                    key={`${p.user_id}-${p.round_window}`}
                    className="border-t border-zinc-800"
                    data-testid={`penalty-row-${p.user_id}-${p.round_window}`}
                  >
                    <td className="px-4 py-3 text-white">
                      <div className="font-semibold">{p.user_name}</div>
                      <div className="text-xs text-zinc-500">{p.user_email}</div>
                    </td>
                    <td className="px-4 py-3 text-zinc-300">R{p.round_window}</td>
                    <td className="px-4 py-3 text-zinc-300">{p.edit_count}</td>
                    <td className="px-4 py-3">
                      {p.waived ? (
                        <span className="text-emerald-400 text-xs font-bold">WAIVED</span>
                      ) : (
                        <span className="text-orange-400 text-xs font-bold">-{p.penalty_amount} pts</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-zinc-400 text-xs">
                      {p.last_edit_at ? new Date(p.last_edit_at).toLocaleString() : '—'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {p.waived ? (
                        <button
                          onClick={() => togglePenalty(p, false)}
                          className="inline-flex items-center gap-1 px-3 py-1.5 bg-orange-500 hover:bg-orange-600 text-white text-xs font-bold"
                          data-testid={`penalty-reapply-${p.user_id}-${p.round_window}`}
                        >
                          <RotateCcw className="w-3 h-3" /> Re-apply
                        </button>
                      ) : (
                        <button
                          onClick={() => togglePenalty(p, true)}
                          className="inline-flex items-center gap-1 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold"
                          data-testid={`penalty-waive-${p.user_id}-${p.round_window}`}
                        >
                          <Check className="w-3 h-3" /> Waive
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
